import { useFeatureFlags } from '@brightdrop/feature-flags-client';

import { useProviderIntegrationsStatusQuery } from '~/api/generated-api';
import { useGmfLinkStatus } from '~/common/hooks/useGmfLinkStatus';
import { useOrgId } from '~/common/hooks/useOrgId';
import {
  getPartnerConfig,
  PARTNER_IDS,
} from '~/features/integrations/partners';

/**
 * Partners the organization has an ACTIVE enrollment with, limited to partners whose
 * flag and visibility rules pass. Shared by the account Integrations pages and their
 * nav item so all agree on when they exist.
 */
export const useLinkedPartners = ({ enabled = true } = {}) => {
  const { getFlag } = useFeatureFlags();
  const organizationsId = useOrgId();
  const {
    status: gmfLinkStatus,
    isLoading: isGmfLinkStatusLoading,
    isError: isGmfLinkStatusError,
  } = useGmfLinkStatus(enabled ? undefined : '');

  const flaggedPartners = Object.values(PARTNER_IDS)
    .map((partnerId) => getPartnerConfig(partnerId))
    .filter((partner) => !!getFlag(partner.flag));
  const partners = flaggedPartners.filter((partner) =>
    partner.shop.isVisible({ gmfLink: { status: gmfLinkStatus } })
  );
  // A failed GMF read only matters when it hid a partner (Fleetio) that might be linked.
  const isVisibilityUnknown =
    isGmfLinkStatusError && partners.length < flaggedPartners.length;

  const { currentData, isFetching, isError } =
    useProviderIntegrationsStatusQuery(
      { 'cs-organization': organizationsId },
      { skip: !enabled || !organizationsId || partners.length === 0 }
    );

  const isInvalidEnvelope =
    currentData != null && (!currentData.success || currentData.result == null);
  const integrations = currentData?.result?.integrations ?? [];
  const linkedPartners = partners.filter((partner) =>
    integrations.some(
      (entry) =>
        entry.provider.provider.providerCode === partner.providerCode &&
        entry.integrationStatus?.status === 'ACTIVE'
    )
  );

  return {
    linkedPartners,
    // Fleetio's visibility waits on the GMF status, so an empty list isn't final until it settles.
    // RTK's isLoading is first-load only; an org change refetches with no currentData for the new org.
    // An empty result being refreshed may be the one a new link turns ACTIVE, so it isn't final either.
    isLoading:
      enabled &&
      (!organizationsId ||
        (isGmfLinkStatusLoading && partners.length < flaggedPartners.length) ||
        (isFetching && (currentData == null || linkedPartners.length === 0))),
    isFetching,
    isError: enabled && (isError || isInvalidEnvelope || isVisibilityUnknown),
  };
};
